import React, { useState } from 'react';
import {
  AccountClient,
  GET_VENDOR_CONVERSATION_REPLY_RECIEPIENTS
} from '@nile/graphql';
import { SVGIcon } from '@superops/super-icons';
import { EventEmitterWithIndex } from '@nile/components';
import { useSideConversationActionStore } from '@nile/mangifera/src/StoreManager/store/sideConversationActionStore';
import TicketMailboxSelector from '@nile/mangifera/src/Containers/ChatContainers/TicketMailboxSelector';
import { GET_DEFAULT_SIDE_CONVERSATION_REPLY_TEMPLATE } from '@nile/graphql/src/Query/ticket';
import { useLazyQuery } from '@apollo/client';
import { ReplyTemplateList } from '@nile/mangifera/src/Containers/ChatContainers/Helpers/helpers';
import { useSuperEditor } from '@nile/components/src/Components/RichTextEditor/useSuperEditor';
import { TEMPLATE_TYPES } from '@nile/banyan/src/Constants/notifications';
import { Button, Sheet } from '@superops/ui-design-system';
import TicketAIRephrase from '@nile/mangifera/src/Containers/ChatContainers/TicketAIRephrase';

const SideConversationHeaderAction = (props: any) => {
  const { editorRef } = props;

  const [sheetActive, setSheetActive] = useState(false);

  const toggleSheetActive = () => {
    setSheetActive((prev) => !prev);
  };

  const data = props?.data?.data;

  const mixpanelEventOnCopy = data?.mixpanelEventOnCopy;
  const mixpanelEventOnClick = data?.mixpanelEventOnClick;

  const workItem = data?.workItem;

  const module = workItem?.module;
  const workId = workItem?.workId;
  const conversationId = data?.conversationId;

  const config = props?.data?.config;

  const editorConfig = config?.editor?.storeConfig;
  const { updateTemplate, template } = useSuperEditor(editorConfig);

  const actionConfig = {
    module,
    workId,
    conversationId: conversationId
  };

  const sideConversationActionStore = useSideConversationActionStore(
    actionConfig
  );

  const selectedMailbox = sideConversationActionStore(
    (state) => state.selectedMailbox
  );
  const setSelectedMailbox = sideConversationActionStore(
    (state) => state.setSelectedMailbox
  );

  const [
    getDefaultReplyTemplateContent,
    { loading: replyTemplateLoading }
  ] = useLazyQuery(GET_DEFAULT_SIDE_CONVERSATION_REPLY_TEMPLATE, {
    client: AccountClient
  });

  const getReplyTemplateData = async (variables) => {
    const response = await getDefaultReplyTemplateContent({
      variables
    });
    return response?.data?.getVendorConversationContentFromTemplate;
  };

  const insertContentTimeOutRef = React.useRef(null);
  const replaceContentTimeOutRef = React.useRef(null);

  const replaceContent = (content) => {
    if (replaceContentTimeOutRef.current) {
      clearTimeout(replaceContentTimeOutRef.current);
    }
    replaceContentTimeOutRef.current = setTimeout(() => {
      EventEmitterWithIndex.dispatch('replaceEditorContent', content);
    }, 100);
  };

  const insertContent = (content) => {
    if (insertContentTimeOutRef.current) {
      clearTimeout(insertContentTimeOutRef.current);
    }
    insertContentTimeOutRef.current = setTimeout(() => {
      EventEmitterWithIndex.dispatch('insertEditorContent', content);
    }, 100);
  };

  const populateEditorContentAndAttachments = ({
    content,
    attachments,
    type
  }) => {
    if (type === 'reset') {
      replaceContent(content);
      if (attachments && attachments.length > 0) {
        updateTemplate({
          attachments: attachments
        });
      }
    } else {
      insertContent(content);
      if (attachments && attachments.length > 0) {
        const currentAttachments = template?.attachments || [];
        updateTemplate({
          attachments: [...currentAttachments, ...attachments]
        });
      }
    }
  };

  const onMailboxChange = async (value) => {
    setSelectedMailbox(value);
    const variables = {
      input: {
        workItem: {
          workId: workItem?.workId,
          module: workItem?.module
        },
        mailbox: {
          boxId: value.boxId
        }
      }
    };
    const replyTemplateData = await getReplyTemplateData(variables);
    if (replyTemplateData?.content || replyTemplateData?.attachments) {
      populateEditorContentAndAttachments({
        content: replyTemplateData?.content,
        attachments: replyTemplateData?.attachments,
        type: 'reset'
      });
    }
  };

  const mailboxSelectorVariables = {
    input: {
      workItem: {
        workId: workItem?.workId,
        module: workItem?.module
      }
    }
  };

  const formatOptions = (res) => {
    return res?.getVendorConversationReplyRecipients?.mailboxes?.map((item) => {
      return {
        ...item,
        label: item?.outgoingAddress,
        value: item?.outgoingAddress
      };
    });
  };

  const populateReplyTemplate = async (templateData) => {
    const templateId = templateData?.id;

    if (!templateId) return;

    const variables = {
      input: {
        template: {
          templateId: templateId
        },
        workItem: {
          workId: workItem?.workId,
          module: workItem?.module
        },
        mailbox: selectedMailbox?.boxId
          ? { boxId: selectedMailbox.boxId }
          : undefined
      }
    };

    const replyTemplateData = await getReplyTemplateData(variables);
    if (replyTemplateData?.content || replyTemplateData?.attachments) {
      populateEditorContentAndAttachments({
        content: replyTemplateData?.content,
        attachments: replyTemplateData?.attachments
      });
    }
  };

  return (
    <div className="tw-mb-2">
      <div className="w100 flex actr tw-gap-x-2">
        <div className="w100">
          <TicketMailboxSelector
            value={selectedMailbox}
            onChange={onMailboxChange}
            client={AccountClient}
            query={GET_VENDOR_CONVERSATION_REPLY_RECIEPIENTS}
            formatOptions={formatOptions}
            variables={mailboxSelectorVariables}
            noBorder
          />
        </div>
        <div className="flex actr tw-gap-x-2">
          <TicketAIRephrase
            editorRef={editorRef}
            mixpanelEventOnCopy={mixpanelEventOnCopy}
            mixpanelEventOnClick={mixpanelEventOnClick}
          />
          <Button
            plainSecondary
            onClick={toggleSheetActive}
            disabled={!selectedMailbox}
          >
            <SVGIcon name="ReplyBubble" />
          </Button>
        </div>
      </div>
      <Sheet open={sheetActive} onClose={toggleSheetActive}>
        <ReplyTemplateList
          populateTemplate={populateReplyTemplate}
          toggleSheetActive={toggleSheetActive}
          notificationTemplateType={TEMPLATE_TYPES.sideConversation}
        />
      </Sheet>
    </div>
  );
};

export default SideConversationHeaderAction;
